import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, CheckCircle2, AlertTriangle, Package, User, MapPin, 
  Clock, History, Camera, QrCode, Loader2, X 
} from 'lucide-react';
import { useAutoSave, useFormProgress, useA11yAnnounce } from '../hooks/useFormHelpers';
import { StepProgress, AutoSaveIndicator } from '../components/ui/animated-progress';
import { FileUploader } from '../components/ui/file-uploader';
import { useTheme } from '../contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PURCHASE_SOURCES = [
  'Ebay', 'Worten', 'Miravia', 'Aliexpress', 'Carrefour',
  'Pixmania', 'Temu', 'Amazon', 'ARREGLATELO.ES',
  'ZONADEJUEGO.ES', 'MUNDODESONRISAS.ES'
];

const SPANISH_COMMUNITIES = [
  'Andalucía', 'Aragón', 'Asturias', 'Baleares', 'Canarias',
  'Cantabria', 'Castilla-La Mancha', 'Castilla y León', 'Cataluña',
  'Extremadura', 'Galicia', 'La Rioja', 'Madrid', 'Murcia',
  'Navarra', 'País Vasco', 'Valencia', 'Ceuta', 'Melilla'
];

const INITIAL_FORM = {
  full_name: '',
  street_name: '',
  street_number: '',
  piso: '',
  puerta: '',
  postal_code: '',
  comunidad: '',
  municipio: '',
  country: 'España',
  phone: '',
  email: '',
  product_name: '',
  serial_number: '',
  purchase_date: '',
  purchase_source: '',
  order_id: '',
  problem_description: ''
};

const WarrantyForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const announce = useA11yAnnounce();
  
  // Form state with auto-save
  const { 
    value: formData, 
    setValue: setFormData, 
    isSaved, 
    lastSaved, 
    clearSaved,
    hasSavedData
  } = useAutoSave('warranty_form_draft', INITIAL_FORM);
  
  // Progress tracking
  const { 
    currentStep, 
    progress, 
    nextStep, 
    prevStep, 
    goToStep 
  } = useFormProgress(4, 1);
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [files, setFiles] = useState([]);
  const [videoFile, setVideoFile] = useState(null);
  const [productHistory, setProductHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [estimation, setEstimation] = useState(null);
  const [loadingEstimation, setLoadingEstimation] = useState(false);
  const [warrantyComparison, setWarrantyComparison] = useState(null);
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);

  // Check for saved data on mount
  useEffect(() => {
    if (hasSavedData()) {
      setShowRestorePrompt(true);
    }
  }, [hasSavedData]);

  // Fetch product history
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/user/product-history`, {
          withCredentials: true
        });
        setProductHistory(response.data);
      } catch (error) {
        console.error('Error fetching product history:', error);
      }
    };
    fetchHistory();
  }, []);

  // Get AI estimation when product info changes
  useEffect(() => {
    const getEstimation = async () => {
      if (formData.product_name && formData.problem_description && formData.purchase_source) {
        setLoadingEstimation(true);
        try {
          const response = await axios.post(
            `${API_URL}/api/warranty/estimate`,
            {
              product_name: formData.product_name,
              problem_description: formData.problem_description,
              purchase_source: formData.purchase_source
            },
            { withCredentials: true }
          );
          setEstimation(response.data);
        } catch (error) {
          console.error('Error getting estimation:', error);
        } finally {
          setLoadingEstimation(false);
        }
      }
    };
    
    const timer = setTimeout(getEstimation, 1000);
    return () => clearTimeout(timer);
  }, [formData.product_name, formData.problem_description, formData.purchase_source]);

  // Check warranty coverage when purchase date changes
  useEffect(() => {
    const checkWarranty = async () => {
      if (formData.purchase_date) {
        try {
          const response = await axios.get(
            `${API_URL}/api/warranty/compare?purchase_date=${formData.purchase_date}`,
            { withCredentials: true }
          );
          setWarrantyComparison(response.data);
        } catch (error) {
          console.error('Error checking warranty:', error);
        }
      }
    };
    checkWarranty();
  }, [formData.purchase_date]);

  const handleChange = useCallback((field, value) => {
    setFormData({ [field]: value });
  }, [setFormData]);

  const applyProductHistory = (product) => {
    setFormData({
      product_name: product.product_name,
      serial_number: product.serial_number || '',
      purchase_date: product.purchase_date || '',
      purchase_source: product.purchase_source || '',
      order_id: product.order_id || ''
    });
    setShowHistory(false);
    toast.success('Datos del producto aplicados');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Create form data for file upload
      const submitData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        submitData.append(key, value);
      });
      
      // Add files
      files.forEach((file, index) => {
        submitData.append(`photo_${index}`, file);
      });
      
      if (videoFile) {
        submitData.append('video', videoFile);
      }

      const response = await axios.post(
        `${API_URL}/api/warranty`,
        formData,
        { withCredentials: true }
      );
      
      setResponseData(response.data);
      setSuccess(true);
      clearSaved(); // Clear auto-saved data
      announce('Solicitud enviada correctamente', 'polite');
      toast.success(t('warranty.success.title'));
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 
        Array.isArray(detail) ? detail.map(e => e.msg || e).join(' ') : 
        'Error al enviar la solicitud';
      toast.error(message);
      announce(`Error: ${message}`, 'assertive');
    } finally {
      setLoading(false);
    }
  };

  const handleNextStep = () => {
    nextStep();
    announce(`Paso ${currentStep + 1} de 4`, 'polite');
  };

  const handlePrevStep = () => {
    prevStep();
    announce(`Paso ${currentStep - 1} de 4`, 'polite');
  };

  const stepLabels = [
    t('warranty.personal'),
    t('warranty.product'),
    'Archivos',
    t('warranty.problem')
  ];

  // Restore prompt
  if (showRestorePrompt && !success) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] dark:bg-gray-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white dark:bg-gray-800 rounded-xl border border-[#E5E7EB] dark:border-gray-700 p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-[#0047FF]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <History className="w-8 h-8 text-[#0047FF]" />
          </div>
          <h2 className="text-xl font-bold mb-2 dark:text-white">Datos guardados encontrados</h2>
          <p className="text-[#52525B] dark:text-gray-400 mb-6">
            Tienes una solicitud sin terminar. ¿Quieres continuar donde lo dejaste?
          </p>
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                clearSaved();
                setShowRestorePrompt(false);
              }}
            >
              Empezar de nuevo
            </Button>
            <Button
              className="flex-1 bg-[#0047FF] hover:bg-[#003BCC]"
              onClick={() => setShowRestorePrompt(false)}
            >
              Continuar
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] dark:bg-gray-900 flex items-center justify-center p-4" data-testid="warranty-success">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-xl border border-[#E5E7EB] dark:border-gray-700 p-8 max-w-lg w-full text-center"
        >
          <div className="w-20 h-20 bg-[#10B981]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-[#10B981]" />
          </div>
          <h1 className="text-2xl font-bold mb-4 dark:text-white">{t('warranty.success.title')}</h1>
          <p className="text-[#52525B] dark:text-gray-400 mb-4">{t('warranty.success.desc')}</p>
          
          {formData.country === 'España' && (
            <div className="bg-[#0047FF]/5 border border-[#0047FF]/20 rounded-lg p-4 mb-4">
              <p className="text-[#0047FF] font-medium">{t('warranty.success.label')}</p>
            </div>
          )}
          
          {estimation && (
            <div className="bg-[#F59E0B]/5 border border-[#F59E0B]/20 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-[#F59E0B]" />
                <span className="font-medium text-[#F59E0B]">Tiempo estimado</span>
              </div>
              <p className="text-2xl font-bold text-[#F59E0B]">{estimation.estimated_days} días</p>
              <p className="text-xs text-[#52525B] dark:text-gray-400">
                ({estimation.min_days}-{estimation.max_days} días, confianza: {estimation.confidence})
              </p>
            </div>
          )}
          
          <p className="text-sm text-[#A1A1AA] mb-6">{t('warranty.success.time')}</p>
          
          {responseData && (
            <div className="bg-[#F5F5F7] dark:bg-gray-700 rounded-lg p-4 mb-6 text-left">
              <p className="font-mono text-sm dark:text-white">
                <span className="text-[#52525B] dark:text-gray-400">Referencia:</span>{' '}
                <span className="font-bold">{responseData.request_id?.slice(0, 12).toUpperCase()}</span>
              </p>
              {responseData.tracking_number && (
                <p className="font-mono text-sm mt-2 dark:text-white">
                  <span className="text-[#52525B] dark:text-gray-400">Tracking:</span>{' '}
                  <span className="font-bold">{responseData.tracking_number}</span>
                </p>
              )}
            </div>
          )}

          {/* QR Code button */}
          {responseData && (
            <div className="mb-6">
              <Button
                variant="outline"
                onClick={() => window.open(`${API_URL}/api/warranty/${responseData.request_id}/qr`, '_blank')}
                className="w-full"
              >
                <QrCode className="w-4 h-4 mr-2" />
                Descargar código QR de seguimiento
              </Button>
            </div>
          )}
          
          <div className="flex gap-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => navigate('/warranty')}
              data-testid="view-warranties-btn"
            >
              {t('dashboard.warranty.check')}
            </Button>
            <Button
              className="flex-1 bg-[#0047FF] hover:bg-[#003BCC]"
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-dashboard-btn"
            >
              {t('nav.dashboard')}
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F7] dark:bg-gray-900 transition-colors" data-testid="warranty-form-page">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-[#E5E7EB] dark:border-gray-700">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center gap-2 text-[#52525B] dark:text-gray-400 hover:text-[#0A0A0A] dark:hover:text-white transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </Link>
          <AutoSaveIndicator isSaved={isSaved} lastSaved={lastSaved} />
        </div>
      </header>

      {/* Form */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-2 dark:text-white">{t('warranty.title')}</h1>
        
        {/* Step Progress */}
        <div className="mb-8">
          <StepProgress 
            currentStep={currentStep} 
            totalSteps={4} 
            labels={stepLabels}
          />
        </div>

        {/* Product History */}
        {productHistory.length > 0 && currentStep === 2 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mb-6"
          >
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-2 text-[#0047FF] text-sm font-medium"
            >
              <History className="w-4 h-4" />
              Usar producto anterior ({productHistory.length})
            </button>
            
            <AnimatePresence>
              {showHistory && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-3 space-y-2"
                >
                  {productHistory.slice(0, 5).map((product) => (
                    <button
                      key={product.product_id}
                      onClick={() => applyProductHistory(product)}
                      className="w-full p-3 bg-white dark:bg-gray-800 rounded-lg border border-[#E5E7EB] dark:border-gray-700 text-left hover:border-[#0047FF] transition-colors"
                    >
                      <p className="font-medium text-sm dark:text-white">{product.product_name}</p>
                      <p className="text-xs text-[#52525B] dark:text-gray-400">
                        {product.purchase_source} • {product.purchase_date}
                      </p>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        <form onSubmit={handleSubmit} aria-label="Formulario de solicitud de garantía">
          <AnimatePresence mode="wait">
            {/* Step 1: Personal Info */}
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white dark:bg-gray-800 rounded-xl border border-[#E5E7EB] dark:border-gray-700 p-6"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-[#0047FF]/10 rounded-lg flex items-center justify-center">
                    <User className="w-5 h-5 text-[#0047FF]" />
                  </div>
                  <h2 className="text-lg font-semibold dark:text-white">{t('warranty.personal')}</h2>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="full_name" className="dark:text-gray-200">{t('warranty.fullName')} *</Label>
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => handleChange('full_name', e.target.value)}
                      className="mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      required
                      aria-required="true"
                      data-testid="full-name-input"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2">
                      <Label htmlFor="street_name" className="dark:text-gray-200">{t('warranty.street')} *</Label>
                      <Input
                        id="street_name"
                        value={formData.street_name}
                        onChange={(e) => handleChange('street_name', e.target.value)}
                        className="mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        required
                        data-testid="street-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="street_number" className="dark:text-gray-200">{t('warranty.number')} *</Label>
                      <Input
                        id="street_number"
                        value={formData.street_number}
                        onChange={(e) => handleChange('street_number', e.target.value)}
                        className="mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        required
                        data-testid="number-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="piso" className="dark:text-gray-200">{t('warranty.piso')}</Label>
                      <Input
                        id="piso"
                        value={formData.piso}
                        onChange={(e) => handleChange('piso', e.target.value)}
                        className="mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        data-testid="piso-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="puerta" className="dark:text-gray-200">{t('warranty.puerta')}</Label>
                      <Input
                        id="puerta"
                        value={formData.puerta}
                        onChange={(e) => handleChange('puerta', e.target.value)}
                        className="mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        data-testid="puerta-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="postal_code" className="dark:text-gray-200">{t('warranty.postalCode')} *</Label>
                      <Input
                        id="postal_code"
                        value={formData.postal_code}
                        onChange={(e) => handleChange('postal_code', e.target.value)}
                        className="mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        required
                        data-testid="postal-code-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="municipio" className="dark:text-gray-200">{t('warranty.municipio')} *</Label>
                      <Input
                        id="municipio"
                        value={formData.municipio}
                        onChange={(e) => handleChange('municipio', e.target.value)}
                        className="mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        required
                        data-testid="municipio-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="comunidad" className="dark:text-gray-200">{t('warranty.comunidad')} *</Label>
                      <Select value={formData.comunidad} onValueChange={(v) => handleChange('comunidad', v)}>
                        <SelectTrigger className="mt-1 dark:bg-gray-700 dark:border-gray-600" data-testid="comunidad-select">
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                        <SelectContent>
                          {SPANISH_COMMUNITIES.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="country" className="dark:text-gray-200">{t('warranty.country')} *</Label>
                      <Input
                        id="country"
                        value={formData.country}
                        onChange={(e) => handleChange('country', e.target.value)}
                        className="mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        required
                        data-testid="country-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone" className="dark:text-gray-200">{t('warranty.phone')} *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        className="mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        required
                        data-testid="phone-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="dark:text-gray-200">{t('warranty.email')} *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        className="mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        required
                        data-testid="email-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <Button
                    type="button"
                    onClick={handleNextStep}
                    className="bg-[#0047FF] hover:bg-[#003BCC]"
                    data-testid="next-step-btn"
                  >
                    {t('common.next')}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 2: Product Info */}
            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white dark:bg-gray-800 rounded-xl border border-[#E5E7EB] dark:border-gray-700 p-6"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-[#8B5CF6]/10 rounded-lg flex items-center justify-center">
                    <Package className="w-5 h-5 text-[#8B5CF6]" />
                  </div>
                  <h2 className="text-lg font-semibold dark:text-white">{t('warranty.product')}</h2>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="product_name" className="dark:text-gray-200">{t('warranty.productName')} *</Label>
                    <Input
                      id="product_name"
                      value={formData.product_name}
                      onChange={(e) => handleChange('product_name', e.target.value)}
                      className="mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      required
                      data-testid="product-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="serial_number" className="dark:text-gray-200">{t('warranty.serialNumber')}</Label>
                    <Input
                      id="serial_number"
                      value={formData.serial_number}
                      onChange={(e) => handleChange('serial_number', e.target.value)}
                      className="mt-1 font-mono dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      data-testid="serial-number-input"
                    />
                    <div className="flex items-start gap-2 mt-2 p-3 bg-[#F59E0B]/10 dark:bg-[#F59E0B]/20 rounded-lg">
                      <AlertTriangle className="w-4 h-4 text-[#F59E0B] mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-[#F59E0B]">{t('warranty.serialWarning')}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="purchase_date" className="dark:text-gray-200">{t('warranty.purchaseDate')} *</Label>
                      <Input
                        id="purchase_date"
                        type="date"
                        value={formData.purchase_date}
                        onChange={(e) => handleChange('purchase_date', e.target.value)}
                        className="mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        required
                        data-testid="purchase-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="purchase_source" className="dark:text-gray-200">{t('warranty.purchaseSource')} *</Label>
                      <Select value={formData.purchase_source} onValueChange={(v) => handleChange('purchase_source', v)}>
                        <SelectTrigger className="mt-1 dark:bg-gray-700 dark:border-gray-600" data-testid="purchase-source-select">
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                        <SelectContent>
                          {PURCHASE_SOURCES.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Warranty comparison */}
                  {warrantyComparison && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-4 rounded-lg ${
                        warrantyComparison.standard_warranty.in_warranty
                          ? 'bg-[#10B981]/10 border border-[#10B981]/20'
                          : 'bg-[#EF4444]/10 border border-[#EF4444]/20'
                      }`}
                    >
                      <p className={`font-medium ${
                        warrantyComparison.standard_warranty.in_warranty ? 'text-[#10B981]' : 'text-[#EF4444]'
                      }`}>
                        {warrantyComparison.recommendation}
                      </p>
                      <p className="text-xs text-[#52525B] dark:text-gray-400 mt-1">
                        {warrantyComparison.standard_warranty.remaining_days} días restantes de garantía estándar
                      </p>
                    </motion.div>
                  )}

                  <div>
                    <Label htmlFor="order_id" className="dark:text-gray-200">{t('warranty.orderId')} *</Label>
                    <Input
                      id="order_id"
                      value={formData.order_id}
                      onChange={(e) => handleChange('order_id', e.target.value)}
                      className="mt-1 font-mono dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      required
                      data-testid="order-id-input"
                    />
                  </div>
                </div>

                <div className="flex justify-between mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePrevStep}
                    data-testid="prev-step-btn"
                  >
                    {t('common.previous')}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleNextStep}
                    className="bg-[#0047FF] hover:bg-[#003BCC]"
                    data-testid="next-step-btn"
                  >
                    {t('common.next')}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Files (Photos/Video) */}
            {currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white dark:bg-gray-800 rounded-xl border border-[#E5E7EB] dark:border-gray-700 p-6"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-[#F59E0B]/10 rounded-lg flex items-center justify-center">
                    <Camera className="w-5 h-5 text-[#F59E0B]" />
                  </div>
                  <h2 className="text-lg font-semibold dark:text-white">Fotos y Vídeo del Problema</h2>
                </div>

                <div className="space-y-6">
                  {/* Photo upload */}
                  <div>
                    <Label className="dark:text-gray-200 mb-2 block">{t('warranty.photos')} (opcional)</Label>
                    <FileUploader
                      accept="image/*"
                      maxFiles={5}
                      maxSizeMB={10}
                      onFilesChange={setFiles}
                      label="Arrastra fotos aquí o haz clic para seleccionar"
                      compressImages={true}
                    />
                  </div>

                  {/* Video upload */}
                  <div>
                    <Label className="dark:text-gray-200 mb-2 block">Vídeo del problema (opcional)</Label>
                    <div className="border-2 border-dashed border-[#E5E7EB] dark:border-gray-600 rounded-xl p-6 text-center">
                      {videoFile ? (
                        <div className="flex items-center justify-between bg-[#F5F5F7] dark:bg-gray-700 p-3 rounded-lg">
                          <span className="text-sm dark:text-white">{videoFile.name}</span>
                          <button
                            onClick={() => setVideoFile(null)}
                            className="text-red-500 hover:text-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <input
                            type="file"
                            accept="video/*"
                            onChange={(e) => setVideoFile(e.target.files[0])}
                            className="hidden"
                            id="video-upload"
                          />
                          <label
                            htmlFor="video-upload"
                            className="cursor-pointer flex flex-col items-center"
                          >
                            <div className="w-12 h-12 bg-[#F59E0B]/10 rounded-full flex items-center justify-center mb-2">
                              <Camera className="w-6 h-6 text-[#F59E0B]" />
                            </div>
                            <p className="text-sm text-[#52525B] dark:text-gray-400">
                              Sube un vídeo corto mostrando el defecto
                            </p>
                            <p className="text-xs text-[#A1A1AA] mt-1">Máximo 50MB</p>
                          </label>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-between mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePrevStep}
                    data-testid="prev-step-btn"
                  >
                    {t('common.previous')}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleNextStep}
                    className="bg-[#0047FF] hover:bg-[#003BCC]"
                    data-testid="next-step-btn"
                  >
                    {t('common.next')}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 4: Problem Description */}
            {currentStep === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white dark:bg-gray-800 rounded-xl border border-[#E5E7EB] dark:border-gray-700 p-6"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-[#10B981]/10 rounded-lg flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#10B981]" />
                  </div>
                  <h2 className="text-lg font-semibold dark:text-white">{t('warranty.problem')}</h2>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="problem_description" className="dark:text-gray-200">{t('warranty.problemDesc')} *</Label>
                    <Textarea
                      id="problem_description"
                      value={formData.problem_description}
                      onChange={(e) => handleChange('problem_description', e.target.value)}
                      className="mt-1 min-h-[150px] dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      required
                      placeholder="Describe detalladamente el problema que tienes con el producto..."
                      data-testid="problem-description-input"
                    />
                  </div>

                  {/* AI Estimation */}
                  {(loadingEstimation || estimation) && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 bg-[#F5F5F7] dark:bg-gray-700 rounded-lg"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-4 h-4 text-[#0047FF]" />
                        <span className="font-medium text-sm dark:text-white">Tiempo estimado de reparación</span>
                      </div>
                      {loadingEstimation ? (
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin text-[#0047FF]" />
                          <span className="text-sm text-[#52525B] dark:text-gray-400">Calculando...</span>
                        </div>
                      ) : estimation && (
                        <>
                          <p className="text-2xl font-bold text-[#0047FF]">{estimation.estimated_days} días</p>
                          <p className="text-xs text-[#52525B] dark:text-gray-400">
                            {estimation.message} (Confianza: {estimation.confidence})
                          </p>
                        </>
                      )}
                    </motion.div>
                  )}
                </div>

                <div className="flex justify-between mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePrevStep}
                    data-testid="prev-step-btn"
                  >
                    {t('common.previous')}
                  </Button>
                  <Button
                    type="submit"
                    className="bg-[#0047FF] hover:bg-[#003BCC]"
                    disabled={loading}
                    data-testid="submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('common.loading')}
                      </>
                    ) : (
                      t('warranty.submit')
                    )}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </form>
      </main>
    </div>
  );
};

export default WarrantyForm;
